<template>
  <div class="video-element-wrapper">
    <div class="element-header">
      <span class="element-type-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polygon points="23 7 16 12 23 17 23 7"></polygon>
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
        </svg>
        <span>Vidéo</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
    
    <div class="video-element">
      <div v-if="element.link || localVideoUrl" class="video-container">
        <video 
          controls 
          class="video-player" 
          :key="videoKey"
          preload="metadata"
          controlsList="nodownload"
        >
          <source :src="localVideoUrl || element.link" type="video/mp4">
          Votre navigateur ne supporte pas l'élément vidéo.
        </video>
        <div class="video-info">
          <span class="video-filename">{{ getFileName(localVideoUrl || element.link) }}</span>
          <button class="change-video-btn" @click="uploadVideo" title="Changer la vidéo" :disabled="isUploading">
            <svg v-if="!isUploading" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="23 7 16 12 23 17 23 7"></polygon>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="uploading-spinner">
              <circle cx="12" cy="12" r="10" opacity="0.3"></circle>
              <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
            </svg>
            <span>{{ isUploading ? 'Upload...' : 'Changer' }}</span>
          </button>
        </div>
      </div>
      <div v-else class="video-placeholder" :class="{ 'uploading': isUploading }">
        <svg v-if="!isUploading" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <polygon points="23 7 16 12 23 17 23 7"></polygon>
          <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
        </svg>
        <svg v-else width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="uploading-spinner">
          <circle cx="12" cy="12" r="10" opacity="0.3"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
        </svg>
        <p v-if="!isUploading">Aucune vidéo sélectionnée</p>
        <p v-else>Upload en cours...</p>
        <button class="upload-video-btn" @click="uploadVideo" :disabled="isUploading">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7,10 12,15 17,10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <span>Sélectionner une vidéo MP4 (max 100Mo)</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { axiosPeelo } from '@/utils/axios'
import { apiBaseUrl } from '@/config'
import { createTrackedLogger } from '@/utils/logger'

export default {
  name: 'VideoElement',
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isUploading: false,
      localVideoUrl: null, // URL locale pour afficher la vidéo sélectionnée
      videoKey: Date.now() // Clé pour forcer le rechargement du lecteur vidéo
    }
  },
  methods: {
    // Méthode pour déclencher l'upload de vidéo
    uploadVideo() {
       // Empêcher les clics multiples pendant l'upload
      if (this.isUploading) {
         return;
      }
      
      // Créer un élément input file dynamique
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.mp4,video/mp4';
      fileInput.style.display = 'none';
      
      // Ajouter l'événement de changement
      fileInput.addEventListener('change', (event) => {
         this.handleVideoUpload(event);
      });
      
      // Ajouter au DOM, déclencher le clic, puis supprimer
      document.body.appendChild(fileInput);
      fileInput.click();
      setTimeout(() => {
        if (document.body.contains(fileInput)) {
          document.body.removeChild(fileInput);
        }
      }, 1000);
    },
    
    // Méthode pour gérer l'upload de vidéo
    async handleVideoUpload(event) {
      // Créer un logger avec ID unique pour cet upload
      const { id: uploadId, logger } = createTrackedLogger('VideoUpload');
      logger.info('Début de l\'upload vidéo');
      
       const file = event.target.files[0];
       if (!file) {
         logger.warn('Aucun fichier sélectionné');
         return;
      }
      
      logger.file('Fichier sélectionné:', {
        name: file.name,
        size: `${(file.size / 1024 / 1024).toFixed(2)} Mo`,
        type: file.type
      });
      
      // Vider complètement le cache vidéo avant de commencer
      this.clearVideoCache();
      // Nettoyer l'ancienne URL locale si elle existe
      if (this.localVideoUrl) {
         URL.revokeObjectURL(this.localVideoUrl);
        this.localVideoUrl = null;
      }
      
       // Vérifier que c'est bien un fichier vidéo MP4
      const fileName = file.name.toLowerCase();
      const allowedTypes = ['video/mp4'];
      const allowedExtensions = ['.mp4'];
      
      const isValidType = allowedTypes.includes(file.type);
      const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!isValidType || !isValidExtension) {
        this.$emit('notification', {
          message: "Format non autorisé. Seuls les fichiers MP4 sont acceptés.",
          type: "error"
        });
        logger.warn('Format de fichier non autorisé:', { type: file.type, name: file.name });
        return;
      }
      
      // Vérifier la taille du fichier (100 Mo maximum pour la vidéo, selon le serveur)
      const maxSizeInBytes = 100 * 1024 * 1024; // 100 Mo en bytes
      const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      
      if (file.size > maxSizeInBytes) {
        this.$emit('notification', {
          message: `La vidéo est trop volumineuse (${fileSizeInMB} Mo). Taille maximum autorisée : 100 Mo`,
          type: "error"
        });
        logger.warn('Fichier trop volumineux:', { size: fileSizeInMB + ' Mo' });
        return;
      }
      
      // Démarrer l'état de chargement
      this.isUploading = true;
      
      // Attendre un peu pour que le nettoyage soit effectif
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Créer une URL locale pour afficher la vidéo immédiatement
      this.localVideoUrl = URL.createObjectURL(file);
      logger.info('URL locale créée pour prévisualisation');
      
       try {
        // Créer FormData pour l'upload
        const formData = new FormData();
        formData.append('videofromplayground', file);
        
         // Récupérer le token d'authentification
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Session expirée, veuillez vous reconnecter');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        
           // Configuration des headers avec timeout étendu pour les vidéos
         const config =  {
            headers: {
             'Authorization': `Bearer ${token}`,
             'Content-Type': 'multipart/form-data'
            },
            timeout: 300000, // 5 minutes
             
          };
        
           // Horodatage avant l'envoi
        const startTime = Date.now();
        logger.info('Début de l\'envoi vers le serveur', {
          endpoint: '/strong/upload/video/block',
          fileSize: fileSizeInMB + ' Mo',
          timeout: 3000000
        });  
        
         // Envoyer la vidéo au serveur
        const response = await axiosPeelo.post(
          '/strong/upload/video/block',
          formData,
          config
        );
        
        const endTime = Date.now();
        const uploadDuration = ((endTime - startTime) / 1000).toFixed(2);
        logger.success(`Upload terminé en ${uploadDuration}s`);
        
         if (response.data && response.data.success) {
          // Le serveur retourne le chemin complet comme "public/audios/videosfromplayground/video-123456789.mp4"
          let cleanFilePath = response.data.filePath;
          
          const videoUrl = `${apiBaseUrl}/${cleanFilePath}`;
          
          logger.info('Chemin original du serveur:', response.data.filePath);
          logger.info('Chemin nettoyé:', cleanFilePath);
          logger.info('URL vidéo finale:', videoUrl);
                   // Attendre que le serveur termine le traitement du fichier vidéo
          await new Promise(resolve => setTimeout(resolve, 5000));
          
           // Mettre à jour l'élément avec le lien de la vidéo du serveur
          const updatedElement = { ...this.element, link: videoUrl };
          
          // Émettre l'événement de mise à jour
          this.$emit('update', updatedElement);
          
          // Émettre une notification de succès
          this.$emit('notification', {
            message: "Vidéo uploadée avec succès",
            type: "success"
          });
          
          logger.success('Vidéo intégrée avec succès');
          
           } else {
          throw new Error(response.data?.message || 'Erreur lors de l\'upload');
        }
        
      } catch (error) {
        logger.error('Erreur lors de l\'upload de la vidéo:', error);
        logger.error('Erreur upload complète:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            timeout: error.config?.timeout
          }
        });
        
        let errorMessage = 'Erreur lors de l\'upload de la vidéo';
        
        if (error.response) {
          // Erreur de réponse du serveur
          if (error.response.status === 502) {
            errorMessage = 'Erreur serveur (502): Le serveur ne peut pas traiter la requête. Vérifiez la taille du fichier ou la configuration serveur.';
          } else {
            errorMessage = error.response.data?.message || 
                          error.response.data?.error || 
                          `Erreur serveur (${error.response.status})`;
          }
        } else if (error.request) {
          // Erreur de réseau ou CORS
          if (error.message === 'Network Error') {
            errorMessage = 'Erreur réseau : Problème CORS ou timeout. Vérifiez la configuration serveur pour les uploads vidéo.';
          } else {
            errorMessage = 'Impossible de contacter le serveur';
          }
        } else {
          // Autre erreur
          errorMessage = error.message || errorMessage;
        }
        
        // Émettre une notification d'erreur
        this.$emit('notification', {
          message: errorMessage,
          type: "error"
        });
        
      } finally {
        // Arrêter l'état de chargement
        this.isUploading = false;
        logger.info('Fin du processus d\'upload');
      }
    },
    
    getFileName(url) {
      if (!url) return 'Fichier vidéo';
      const parts = url.split('/');
      return parts[parts.length - 1];
    },
    
    // Méthode pour vider le cache vidéo complètement
    clearVideoCache() {
       // Changer la clé pour forcer le rechargement du composant vidéo
      this.videoKey = Date.now();
      
      // Si il y a une URL locale, la nettoyer
      if (this.localVideoUrl) {
        URL.revokeObjectURL(this.localVideoUrl);
        this.localVideoUrl = null;
      }
      
      // Forcer le garbage collection si possible
      if (window.gc) {
        window.gc();
      }
    }
  },
  
  // Nettoyer l'URL locale quand le composant est détruit
  beforeUnmount() {
    if (this.localVideoUrl) {
      URL.revokeObjectURL(this.localVideoUrl);
    }
  }
}
</script>

<style scoped>
.video-element-wrapper {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(249, 250, 251, 0.9));
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  margin-bottom: 20px;
}

.video-element-wrapper:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(145deg, rgba(249, 250, 251, 0.8), rgba(243, 244, 246, 0.6));
  border-bottom: 1px solid rgba(229, 231, 235, 0.5);
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #dc2626;
  font-size: 0.875rem;
}

.element-type-badge i {
  color: #dc2626;
}

.element-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.8));
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.action-btn.delete-btn:hover {
  background: linear-gradient(145deg, rgba(239, 68, 68, 0.15), rgba(239, 68, 68, 0.05));
  color: #ef4444;
}

.video-element {
  padding: 16px;
}

.video-placeholder {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  color: #666;
  border: 2px dashed #dee2e6;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
}

.video-placeholder:hover {
  background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
  border-color: #adb5bd;
}

.video-placeholder p {
  margin: 0;
  font-size: 0.9rem;
}

.video-container {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.video-player {
  width: 100%;
  height: auto;
  border-radius: 12px;
  background: #000;
  max-height: 400px;
}

.video-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.video-filename {
  font-size: 0.875rem;
  color: #dc2626;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;
}

.change-video-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(220, 38, 38, 0.2);
}

.change-video-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
  box-shadow: 0 4px 8px rgba(220, 38, 38, 0.3);
  transform: translateY(-1px);
}

.change-video-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.upload-video-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 8px rgba(220, 38, 38, 0.2);
  margin-top: 8px;
}

.upload-video-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
  box-shadow: 0 6px 12px rgba(220, 38, 38, 0.3);
  transform: translateY(-2px);
}

.upload-video-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.uploading-spinner {
  animation: spin 1s linear infinite;
}

.video-placeholder.uploading {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-color: #f87171;
  color: #dc2626;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
